import React, {
  useEffect,
  useContext,
  useMemo,
  useState,
  ReactNode,
} from 'react';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { AppContext } from '@thd-nucleus/app-render';
import { useDataModel } from '@thd-nucleus/data-sources';
import {
  storeFrontRouteGroups,
  b2bRouteGroups,
  routeGroups,
} from '../constants/routes';
import {
  FeaturesMap,
  INavRoute,
  IRouteGroup,
  TConcreteRoute,
  TConcreteRouteGroup,
} from '../models/navigation';
import { useCustomerInfo } from '../hooks/useCustomerInfo';
import { findLongestPrefix } from '../utils/routes';

interface CustomerAccountNavigationWrapperProps {
  skipQuery?: boolean;
  children: (props: ChildrenProps) => ReactNode;
  isStoreFront: boolean;
}

export interface ChildrenProps {
  concreteRouteGroups: TConcreteRouteGroup[];
  activeRoute: string;
  triggerLinkAnalytics: (analyticsName: string) => void;
}

export const CustomerAccountNavigationWrapper = ({
  skipQuery = false,
  children,
  isStoreFront,
}: CustomerAccountNavigationWrapperProps) => {
  useEffect(() => {
    window.LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger(
      'customer-account-navigation.ready',
    );
  }, []);
  const { channel } = useContext(ExperienceContext);
  const { instance } = useContext(AppContext);
  const {
    ENABLE_NUCLEUS_QUOTE_EXPERIENCE,
    ENABLE_PROJECTS_PAGE,
    disableProjectBenefitCheck,
  } = instance?.expConfiguration || {};
  const customer = useCustomerInfo();
  const { customerType, svocID: customerAccountId, userID: userId } = customer;
  const isB2B = customerType?.toUpperCase() === 'B2B';
  const channelId = channel === 'mobile' ? 2 : 1;
  const [activeRoute, setActiveRoute] = useState('');

  const { data: loyaltyData } = useDataModel('loyaltyMembershipDetails', {
    variables: { userId, customerAccountId, channelId },
    skip: !isB2B || !userId || !customerAccountId || skipQuery,
    ssr: false,
    context: { withAuth: true },
  });

  const { data: userData } = useDataModel('userState', {
    variables: { userId, channelId },
    skip: !isB2B || !userId || skipQuery,
    ssr: false,
    context: { withAuth: true },
  });

  const triggerLinkAnalytics = (analyticsName: string) => {
    window.LIFE_CYCLE_EVENT_BUS.trigger(
      'customer-account-navigation.link-click',
      {
        route: analyticsName,
      },
    );
  };

  const { allRoutes, concreteRouteGroups } = useMemo(() => {
    const featuresMap: FeaturesMap = {
      ENABLE_NUCLEUS_QUOTE_EXPERIENCE:
        ENABLE_NUCLEUS_QUOTE_EXPERIENCE === 'true',
      ENABLE_PROJECTS_PAGE: ENABLE_PROJECTS_PAGE === '1',
      disableProjectBenefitCheck: disableProjectBenefitCheck === 'true',
    };

    const shouldShow = (route: INavRoute) => {
      return typeof route.shouldShow === 'function'
        ? route.shouldShow(
            loyaltyData?.loyaltyMembershipDetails || {},
            userData?.userState || {},
            customer,
            featuresMap,
          )
        : route.shouldShow;
    };

    const generateRealPath = (route: INavRoute): TConcreteRoute => {
      return {
        ...route,
        path:
          typeof route.path === 'function'
            ? route.path(featuresMap)
            : route.path,
      };
    };

    let groups: IRouteGroup[];
    if (isStoreFront) {
      groups = storeFrontRouteGroups;
    } else if (isB2B) {
      groups = b2bRouteGroups;
    } else {
      groups = routeGroups;
    }

    return groups.reduce<{
      allRoutes: TConcreteRoute[];
      concreteRouteGroups: TConcreteRouteGroup[];
    }>(
      (
        {
          allRoutes: allRoutesAcc,
          concreteRouteGroups: concreteRouteGroupsAcc,
        },
        group,
      ) => {
        const links = group.links.filter(shouldShow).map(generateRealPath);
        allRoutesAcc.push(...links);
        concreteRouteGroupsAcc.push({ ...group, links });
        return {
          allRoutes: allRoutesAcc,
          concreteRouteGroups: concreteRouteGroupsAcc,
        };
      },
      { allRoutes: [], concreteRouteGroups: [] },
    );
  }, [
    ENABLE_NUCLEUS_QUOTE_EXPERIENCE,

    ENABLE_PROJECTS_PAGE,
    disableProjectBenefitCheck,
    isB2B,
    loyaltyData?.loyaltyMembershipDetails,
    userData?.userState,
    customer,
    isStoreFront,
  ]);

  useEffect(() => {
    setActiveRoute(findLongestPrefix(allRoutes, window.location.pathname));
  }, [allRoutes]);

  const childProps: ChildrenProps = {
    concreteRouteGroups,
    activeRoute,
    triggerLinkAnalytics,
  };

  return <>{children(childProps)}</>;
};

CustomerAccountNavigationWrapper.defaultProps = {
  skipQuery: false,
};
